import { useNavigate } from "@tanstack/react-router";
import { type FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useApi } from "~/api/provider";
import {
  useCollections,
  useCreateCollection,
  useCreateDocument,
  useCreateDocumentFromTemplate,
  useCreateShareLink,
  useDeleteDocument,
  useDocument,
  useDocumentShare,
  useEnsureStarterTemplates,
  usePublishDocument,
  useRevokeShareLink,
  useTemplates,
  useUnpublishDocument,
  useUpdateDocument,
  useUpdateShareLink,
} from "~/api/queries";
import { ABILITY, type Document } from "~/api/types";
import { stashCompose } from "~/features/documents/composeHandoff";
import { MoveDocumentDialog } from "~/features/documents/MoveDocumentDialog";
import { ProjectDialog } from "~/features/projects/ProjectDialog";
import { Button } from "~/foundation/components/Button";
import { ConfirmDialog, Dialog } from "~/foundation/components/Dialog";
import { Input } from "~/foundation/components/Input";
import { Spinner } from "~/foundation/components/Spinner";
import { Switch } from "~/foundation/components/Switch";
import { Text } from "~/foundation/components/Text";
import { useAbilitiesLookup } from "~/policies/usePolicy";
import { KeyboardShortcutsDialog } from "~/shell/KeyboardShortcutsDialog";
import { useShellStore } from "~/shell/store";

/**
 * New document — a name, or a description.
 *
 * The kms delta split one auto-creating effect into two paths: a template path that creates on
 * mount, and a **compose form** for everything else. Slate has no `/documents/new` route and
 * should not add one, so the form lives in the dialog that already asks for a title.
 *
 * *Just name it* is always enabled; *Compose* needs instructions and hands them to the new
 * document through session storage (§A4.2) so a reload cannot re-run the compose.
 *
 * The four starter templates are the endpoint's first caller in the product's life
 * (§0 C1/D9). `templates.ensureStarters` is idempotent by title server-side, which is why it
 * can sit behind a button rather than a one-time migration.
 */
function NewDocumentDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [instructions, setInstructions] = useState("");
  const [collectionId, setCollectionId] = useState<string>("");
  const [starters, setStarters] = useState<Document[]>([]);
  const { data: collections } = useCollections();
  // W10 §9 — the workspace's real templates. `documents.list` cannot see them without
  // `template: true`, which is why this is its own query and not a filter on the document list.
  const { data: templates } = useTemplates();
  const createDocument = useCreateDocument();
  const createFromTemplate = useCreateDocumentFromTemplate();
  const ensureStarters = useEnsureStarterTemplates();
  const navigate = useNavigate();

  const create = (extra: { text?: string } = {}, compose = false) => {
    if (!title.trim()) {
      return;
    }
    createDocument.mutate(
      {
        title: title.trim(),
        collectionId: collectionId || null,
        ...(extra.text ? { text: extra.text } : {}),
      },
      {
        onSuccess: (document) => {
          if (compose) {
            stashCompose(document.id, instructions.trim());
          }
          onClose();
          toast.success(t("Created “{{title}}”", { title: document.title }));
          void navigate({ to: "/documents/$documentId", params: { documentId: document.id } });
        },
      },
    );
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    create({}, instructions.trim().length > 0);
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("New document")}
      closeLabel={t("Close")}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button
            variant="neutral"
            disabled={createDocument.isPending || title.trim() === ""}
            onClick={() => create()}
          >
            {t("Just name it")}
          </Button>
          <Button
            variant="primary"
            disabled={createDocument.isPending || title.trim() === "" || instructions.trim() === ""}
            onClick={submit}
          >
            {t("Compose")}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-3">
        <Input
          label={t("Title")}
          value={title}
          autoFocus
          fullWidth
          onChange={(event) => setTitle(event.target.value)}
          placeholder={t("Untitled")}
        />
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="new-document-collection"
            className="text-small font-medium text-text-secondary"
          >
            {t("Collection")}
          </label>
          <select
            id="new-document-collection"
            value={collectionId}
            onChange={(event) => setCollectionId(event.target.value)}
            className="h-8 rounded-sm border border-input-border bg-bg px-2 text-body text-text"
          >
            <option value="">{t("None — keep as a draft")}</option>
            {collections?.map((collection) => (
              <option key={collection.id} value={collection.id}>
                {collection.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="new-document-compose"
            className="text-small font-medium text-text-secondary"
          >
            {t("What should this document be?")}
          </label>
          <textarea
            id="new-document-compose"
            rows={6}
            value={instructions}
            onChange={(event) => setInstructions(event.target.value)}
            placeholder={t("A one-page brief for the migration, with risks and a rollback plan.")}
            className="scrollbar-thin rounded-sm border border-input-border bg-bg px-2 py-1.5 text-body text-text focus:border-input-border-focus focus:outline-none"
          />
        </div>

        {/* W10 §9 — start from a template. ONE call: the server copies the template's
            content, text, icon and colour, so nothing is read and re-posted here. */}
        {templates && templates.length > 0 ? (
          <div className="flex flex-col gap-1.5">
            <span className="text-small font-medium text-text-secondary">
              {t("Start from a template")}
            </span>
            <div className="flex flex-wrap items-center gap-1.5">
              {templates.map((template) => (
                <button
                  key={template.id}
                  type="button"
                  disabled={createFromTemplate.isPending}
                  onClick={() =>
                    createFromTemplate.mutate(
                      {
                        templateId: template.id,
                        ...(title.trim() ? { title: title.trim() } : {}),
                        ...(collectionId ? { collectionId } : {}),
                      },
                      {
                        onSuccess: (document) => {
                          onClose();
                          toast.success(t("Created “{{title}}”", { title: document.title }));
                          void navigate({
                            to: "/documents/$documentId",
                            params: { documentId: document.id },
                          });
                        },
                      },
                    )
                  }
                  className="h-7 rounded-full border border-divider bg-bg-secondary px-2.5 text-small text-text hover:border-accent hover:text-accent disabled:opacity-50"
                >
                  {template.emoji ? `${template.emoji} ` : ""}
                  {template.title}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-1.5">
          {starters.length === 0 ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={ensureStarters.isPending}
              onClick={() =>
                ensureStarters.mutate(undefined, {
                  onSuccess: (created) => setStarters(created),
                })
              }
            >
              {ensureStarters.isPending ? t("Adding…") : t("Start from a template")}
            </Button>
          ) : (
            starters.map((starter) => (
              <button
                key={starter.id}
                type="button"
                onClick={() => {
                  if (!title.trim()) {
                    setTitle(starter.title);
                  }
                  create({ text: starter.text });
                }}
                className="h-7 rounded-full border border-divider bg-bg-secondary px-2.5 text-small text-text hover:border-accent hover:text-accent"
              >
                {starter.title}
              </button>
            ))
          )}
        </div>
      </form>
    </Dialog>
  );
}

/**
 * The welcome guide, re-entered on purpose (§A4.5).
 *
 * Only collections the viewer can `update` are listed, because that is exactly what the server
 * gates `collections.seed-welcome` on — offering one that will 403 is a button to nothing. The
 * `wizard.reentered` telemetry is fired and its failure swallowed: a rate-limited event write
 * must never break re-entry.
 */
function WelcomeGuideDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const api = useApi();
  const navigate = useNavigate();
  const { data: collections } = useCollections();
  const abilities = useAbilitiesLookup();
  const [busy, setBusy] = useState(false);

  const writable = (collections ?? []).filter(
    (collection) => abilities(collection.id)[ABILITY.collectionUpdate] === true,
  );

  const seed = async (collectionId: string) => {
    setBusy(true);
    try {
      const result = await api.wizard.seedWelcome(collectionId);
      void api.wizard.recordEvent("wizard.reentered", { collectionId });
      if (result.archived === true) {
        toast.message(t("The welcome guide for this collection is in the archive"));
        return;
      }
      onClose();
      if (result.documentId) {
        void navigate({
          to: "/documents/$documentId",
          params: { documentId: result.documentId },
        });
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("Something went wrong"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("Open the welcome guide")}
      closeLabel={t("Close")}
      size="sm"
      footer={
        <Button variant="ghost" onClick={onClose}>
          {t("Cancel")}
        </Button>
      }
    >
      {writable.length === 0 ? (
        <Text variant="secondary">
          {t("You don't have a collection you can add a welcome guide to yet")}
        </Text>
      ) : (
        <div className="flex flex-col gap-1.5">
          {writable.map((collection) => (
            <Button
              key={collection.id}
              variant="neutral"
              fullWidth
              disabled={busy}
              onClick={() => void seed(collection.id)}
            >
              {collection.emoji ? `${collection.emoji} ` : ""}
              {collection.name}
            </Button>
          ))}
        </div>
      )}
    </Dialog>
  );
}

function NewCollectionDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const createCollection = useCreateCollection();
  const navigate = useNavigate();

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim()) {
      return;
    }
    createCollection.mutate(
      { name: name.trim() },
      {
        onSuccess: (collection) => {
          onClose();
          toast.success(t("Created “{{title}}”", { title: collection.name }));
          void navigate({
            to: "/collections/$collectionId",
            params: { collectionId: collection.id },
          });
        },
      },
    );
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("New collection")}
      closeLabel={t("Close")}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button
            variant="primary"
            disabled={createCollection.isPending || name.trim() === ""}
            onClick={submit}
          >
            {t("Create")}
          </Button>
        </>
      }
    >
      <form onSubmit={submit}>
        <Input
          label={t("Name")}
          value={name}
          autoFocus
          fullWidth
          onChange={(event) => setName(event.target.value)}
        />
      </form>
    </Dialog>
  );
}

function RenameDocumentDialog({
  documentId,
  onClose,
}: {
  documentId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { data: document } = useDocument(documentId);
  const [title, setTitle] = useState("");
  const updateDocument = useUpdateDocument();

  useEffect(() => {
    if (document) {
      setTitle(document.title);
    }
  }, [document]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!title.trim()) {
      return;
    }
    updateDocument.mutate(
      { id: documentId, data: { title: title.trim() } },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("Rename document")}
      closeLabel={t("Close")}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button variant="primary" disabled={updateDocument.isPending} onClick={submit}>
            {t("Save")}
          </Button>
        </>
      }
    >
      <form onSubmit={submit}>
        <Input
          label={t("Title")}
          value={title}
          autoFocus
          fullWidth
          onChange={(event) => setTitle(event.target.value)}
        />
      </form>
    </Dialog>
  );
}

/**
 * W10 §4 — the share dialog, now writing.
 *
 * Three things this dialog says that the previous local-state version could not:
 *
 * 1. **A link exists or it does not.** `shares.info` answers 204 for a document that has never
 *    been shared, so `null` is the ordinary first state — not a failure, and not a link with
 *    empty fields. Nothing is created until someone asks for one.
 * 2. **Published is not the same as shared.** A link with `published:false` is a workspace
 *    link; `published:true` is what makes it readable without signing in. The two switches are
 *    separate on the wire and separate here.
 * 3. **The document's own published state is a different question again.** `documents.publish`
 *    / `.unpublish` move it in and out of the collection's published list, which is why that
 *    row sits apart, under its own heading, gated on `update` (the ability the UNpublish route
 *    checks) rather than on `share`.
 *
 * `shares.create` is an upsert, so pressing "Create link" twice cannot produce two links.
 */
function ShareDocumentDialog({ documentId, onClose }: { documentId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const { data: document } = useDocument(documentId);
  const { data: share, isLoading } = useDocumentShare(documentId);
  const createShare = useCreateShareLink();
  const updateShare = useUpdateShareLink();
  const revokeShare = useRevokeShareLink();
  const publish = usePublishDocument();
  const unpublish = useUnpublishDocument();
  const abilities = useAbilitiesLookup();

  const can = abilities(documentId);
  const canUpdate = can[ABILITY.documentUpdate] === true;
  const busy =
    createShare.isPending ||
    updateShare.isPending ||
    revokeShare.isPending ||
    publish.isPending ||
    unpublish.isPending;

  const patch = (next: { published?: boolean; includeChildDocuments?: boolean }) => {
    if (share) {
      updateShare.mutate({ id: share.id, ...next });
    } else {
      createShare.mutate({ documentId, ...next });
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("Share “{{title}}”", { title: document?.title ?? "" })}
      closeLabel={t("Close")}
      size="sm"
      footer={
        <Button variant="ghost" onClick={onClose}>
          {t("Done")}
        </Button>
      }
    >
      {isLoading ? (
        <Spinner label={t("Loading")} />
      ) : (
        <div className="flex flex-col gap-3">
          {share ? (
            <>
              <Switch
                checked={share.published}
                disabled={busy}
                onCheckedChange={(published) => patch({ published })}
                label={t("Publish to the web")}
                description={t("Anyone with the link can read it, without signing in.")}
              />
              <div className="border-t border-divider pt-3">
                <Switch
                  checked={share.includeChildDocuments}
                  disabled={busy}
                  onCheckedChange={(includeChildDocuments) => patch({ includeChildDocuments })}
                  label={t("Include nested documents")}
                  description={t("Documents nested under this one open from the same link.")}
                />
              </div>

              <div className="mt-1 flex items-center gap-2">
                <Input value={share.url} readOnly fullWidth aria-label={t("Share link")} />
                <Button
                  onClick={() => {
                    void navigator.clipboard?.writeText(share.url);
                    toast.success(t("Link copied"));
                  }}
                >
                  {t("Copy")}
                </Button>
              </div>

              <Text variant="small">
                {share.published
                  ? t("Anyone with this link can read the document.")
                  : t("Only people with workspace access can open this link.")}
              </Text>

              <div className="border-t border-divider pt-3">
                <Button
                  variant="danger"
                  disabled={busy}
                  onClick={() =>
                    revokeShare.mutate(share.id, {
                      onSuccess: () => toast.success(t("Link revoked")),
                    })
                  }
                >
                  {t("Revoke link")}
                </Button>
              </div>
            </>
          ) : (
            <>
              <Text variant="secondary">{t("This document has no share link yet.")}</Text>
              <div>
                <Button
                  variant="primary"
                  disabled={busy}
                  onClick={() =>
                    createShare.mutate(
                      { documentId },
                      { onSuccess: () => toast.success(t("Share link created")) },
                    )
                  }
                >
                  {t("Create a link")}
                </Button>
              </div>
            </>
          )}

          {/* A different question from sharing: whether the document is in the collection's
              published list at all. Gated on `update`, which is what the unpublish route checks. */}
          {canUpdate && document ? (
            <div className="flex items-center justify-between gap-3 border-t border-divider pt-3">
              <div className="min-w-0">
                <Text variant="small" className="font-medium text-text">
                  {document.publishedAt ? t("Published in the workspace") : t("Draft")}
                </Text>
                <Text variant="small" className="text-text-tertiary">
                  {document.publishedAt
                    ? t("Everyone with access to the collection can find it.")
                    : t("Only you and collaborators can find it.")}
                </Text>
              </div>
              <Button
                variant="neutral"
                disabled={busy}
                onClick={() =>
                  document.publishedAt
                    ? unpublish.mutate(documentId, {
                        onSuccess: () => toast.success(t("Moved back to drafts")),
                      })
                    : publish.mutate(documentId, {
                        onSuccess: () => toast.success(t("Published")),
                      })
                }
              >
                {document.publishedAt ? t("Unpublish") : t("Publish")}
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </Dialog>
  );
}

function DeleteDocumentDialog({
  documentId,
  onClose,
}: {
  documentId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { data: document } = useDocument(documentId);
  const deleteDocument = useDeleteDocument();
  const navigate = useNavigate();

  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("Delete document")}
      description={t("“{{title}}” will be moved to the trash.", { title: document?.title ?? "" })}
      confirmLabel={t("Delete")}
      cancelLabel={t("Cancel")}
      closeLabel={t("Close")}
      dangerous
      busy={deleteDocument.isPending}
      onConfirm={() =>
        deleteDocument.mutate(documentId, {
          onSuccess: () => {
            onClose();
            toast.success(t("Document deleted"));
            void navigate({ to: "/documents" });
          },
        })
      }
    />
  );
}

/**
 * Actions open dialogs by name, not by rendering them — which is what lets the command bar,
 * a context menu and a keyboard shortcut all reach the same dialog.
 */
export function DialogHost() {
  const activeDialog = useShellStore((state) => state.activeDialog);
  const closeDialog = useShellStore((state) => state.closeDialog);

  if (!activeDialog) {
    return null;
  }

  switch (activeDialog.kind) {
    case "newDocument":
      return <NewDocumentDialog onClose={closeDialog} />;
    case "newCollection":
      return <NewCollectionDialog onClose={closeDialog} />;
    case "newProject":
      return <ProjectDialog onClose={closeDialog} />;
    case "renameDocument":
      return <RenameDocumentDialog documentId={activeDialog.documentId} onClose={closeDialog} />;
    case "shareDocument":
      return <ShareDocumentDialog documentId={activeDialog.documentId} onClose={closeDialog} />;
    case "moveDocument":
      return <MoveDocumentDialog documentId={activeDialog.documentId} onClose={closeDialog} />;
    case "deleteDocument":
      return <DeleteDocumentDialog documentId={activeDialog.documentId} onClose={closeDialog} />;
    case "welcomeGuide":
      return <WelcomeGuideDialog onClose={closeDialog} />;
    case "shortcuts":
      return <KeyboardShortcutsDialog open onOpenChange={(open) => !open && closeDialog()} />;
    default:
      return null;
  }
}
